import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ChartAudio, ChartPalette } from '@shared/chart';
import { validateChart } from '@shared/chart';
import type { Difficulty } from '@shared/gameTypes';
import type { GuitarChart } from '@shared/guitarChart';
import { GUITAR_APPROACH_MS_MAX, GUITAR_APPROACH_MS_MIN } from '@shared/guitarChart';
import type { ManiaLane, ManiaNote } from '@shared/maniaNote';
import { SAME_BEAT_EPSILON } from '@shared/maniaNote';
import type { ReplayOutcome } from '@shared/replay';
import { ManiaChartBuilder } from '@shared/engine/mania/maniaChartBuilder';
import { listBuiltinGuitarTracks } from '@shared/builtinGuitarMaps';
import { authFetch } from '../../api';
import { createMap, fetchMapDetail, updateMap } from '../../apiClient/maps';
import { useAuth } from '../../auth/AuthProvider';
import { MyMapsList } from '../../components/MyMapsList';
import { EditorDraftNotice } from '../../components/EditorDraftNotice';
import { useEditorDraft } from '../../hooks/useEditorDraft';
import { editorDraftKey, isGuitarEditorDraft, type GuitarEditorDraft } from '../editorDraft';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { GuitarGameSession } from '../../game/guitar/guitarSession';
import {
  GuitarEditorGrid,
  editorBeatToY,
  editorLaneFromX,
  editorYToBeat,
  snapBeatToGrid,
  LOOP_GUTTER_W,
  type EditorGeometry,
} from '../../game/guitar/editor/guitarEditorGrid';
import {
  useEditorAudioPreview,
  type LoopRegionBeats,
} from '../../hooks/useEditorAudioPreview';
import {
  useEditorLoopUi,
  buildLoopRegion,
  DEFAULT_LOOP_BEATS,
} from '../../hooks/useEditorLoopUi';
import { describeKeyCode, loadKeybindPrefs } from '../../game/keybindPrefs';
import { DEFAULT_KEY_LABELS } from '../../game/guitar/render/guitarRenderConstants';
import '../builder.css';
import './guitarBuilder.css';

interface SaveErrorBody {
  error?: string;
  issues?: { level?: string; message?: string }[] | unknown;
  message?: string;
}

function formatSaveError(err: { body?: SaveErrorBody; message?: string }): string {
  const body = err.body;
  if (!body) {
    return err.message ?? 'Save failed';
  }
  if (body.error === 'slug_taken') {
    return 'That slug is already taken - try a different title.';
  }
  if (Array.isArray(body.issues) && body.issues.length > 0) {
    const messages = body.issues
      .filter((i): i is { message?: string } => typeof i === 'object' && i !== null)
      .map((i) => i.message)
      .filter((m): m is string => typeof m === 'string')
      .slice(0, 3);
    if (messages.length > 0) {
      return `Save failed: ${messages.join('; ')}`;
    }
  }
  if (body.error) {
    return `Save failed: ${body.error}`;
  }
  return err.message ?? 'Save failed';
}

function isSlugTaken(err: unknown): boolean {
  const e = err as { status?: number; body?: { error?: string } };
  return e?.body?.error === 'slug_taken' || e?.status === 409;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

// One option per song — every variant shares the same backing audio, so
// the normal variant is the canonical template the editor copies.
const SONG_TEMPLATES: GuitarChart[] = listBuiltinGuitarTracks().map(
  (t) => t.variants.normal.chart,
);
const DEFAULT_SONG_SLUG = SONG_TEMPLATES[0].slug;

function templateForSlug(slug: string): GuitarChart {
  return SONG_TEMPLATES.find((c) => c.slug === slug) ?? SONG_TEMPLATES[0];
}

function laneLabels(): string[] {
  const prefs = loadKeybindPrefs();
  return ([0, 1, 2, 3] as ManiaLane[]).map((lane) => {
    const code = prefs.guitarLanes[lane]?.[0];
    return code ? describeKeyCode(code) : DEFAULT_KEY_LABELS[lane];
  });
}

// Snap grid options, expressed as subdivisions per beat (1 = on the beat).
const SNAP_DIVISIONS = [1, 2, 3, 4, 6, 8] as const;
const DEFAULT_SNAP = 2;
const PX_PER_BEAT_MIN = 44;
const PX_PER_BEAT_MAX = 220;
const PX_PER_BEAT_DEFAULT = 88;
const PX_PER_BEAT_STEP = 22;
const HISTORY_LIMIT = 60;
const HIT_TOLERANCE_PX = 10;
const DEFAULT_APPROACH_MS = 1450;

interface NotesHistory {
  past: ManiaNote[][];
  future: ManiaNote[][];
}

/**
 * Guitar UGC map creator. A flat 4-lane piano-roll editor over the 12
 * built-in Guitar songs: the author scrubs/places taps, holds and
 * chords on the chosen song's audio, playtests with the real
 * {@link GuitarGameSession}, then publishes to the shared maps backend.
 */
export function GuitarBuilderPage() {
  const { slug: editingSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initialTemplate = templateForSlug(DEFAULT_SONG_SLUG);

  const [title, setTitle] = useState('Untitled');
  const [description, setDescription] = useState('A fresh guitar chart');
  const [slug, setSlug] = useState('untitled');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [songSlug, setSongSlug] = useState(DEFAULT_SONG_SLUG);
  const [audio, setAudio] = useState<ChartAudio>(initialTemplate.audio);
  const [bpm, setBpm] = useState(initialTemplate.bpm);
  const [totalBeats, setTotalBeats] = useState(initialTemplate.totalBeats);
  const [palette, setPalette] = useState<ChartPalette>(initialTemplate.palette);
  const [difficulty, setDifficulty] = useState(3);
  const [approachMs, setApproachMs] = useState(DEFAULT_APPROACH_MS);
  const [isPublic, setIsPublic] = useState(false);

  const [notes, setNotes] = useState<ManiaNote[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hover, setHover] = useState<{ beat: number; lane: ManiaLane } | null>(null);
  const [snap, setSnap] = useState<number>(DEFAULT_SNAP);
  const [pxPerBeat, setPxPerBeat] = useState(PX_PER_BEAT_DEFAULT);
  const [scrollBeat, setScrollBeat] = useState(0);
  const [history, setHistory] = useState<NotesHistory>({ past: [], future: [] });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedSlug, setSavedSlug] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playtest, setPlaytest] = useState(false);
  const [playtestOutcome, setPlaytestOutcome] = useState<ReplayOutcome | null>(null);
  // Bumped to re-run the playtest session effect for "Play again".
  const [playtestNonce, setPlaytestNonce] = useState(0);
  const [keyLabels] = useState<string[]>(() => laneLabels());

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const gridRef = useRef<GuitarEditorGrid | null>(null);
  const scrollBeatRef = useRef(0);
  const notesRef = useRef<ManiaNote[]>([]);
  const historyRef = useRef<NotesHistory>({ past: [], future: [] });
  const dragRef = useRef<{ id: string; headBeat: number; base: ManiaNote[]; changed: boolean } | null>(
    null,
  );
  /** Active loop-gutter drag (beats run up the canvas). */
  const loopDragRef = useRef<{ anchorBeat: number; region: LoopRegionBeats } | null>(null);

  const preview = useEditorAudioPreview({
    src: audio.src,
    bpm,
    offsetMs: audio.offsetMs,
    totalBeats,
  });
  const playing = preview.isPlaying;
  // Default loop (L / button): a window anchored at the top of the view.
  const defaultLoopRegion = useCallback(
    () =>
      buildLoopRegion(
        Math.round(scrollBeatRef.current),
        Math.round(scrollBeatRef.current) + DEFAULT_LOOP_BEATS,
        totalBeats,
      ),
    [totalBeats],
  );
  const loopUi = useEditorLoopUi({
    preview,
    playFromBeat: () =>
      scrollBeatRef.current >= totalBeats ? 0 : scrollBeatRef.current,
    defaultLoopRegion,
    onError: setSaveError,
  });
  // setLoopDraft drives the in-drag loop preview; refs below are read by the
  // once-attached wheel / keydown listeners.
  const { togglePlay, toggleLoop, setLoopDraft, shownLoopRegion } = loopUi;
  const { previewRef, togglePlayRef, toggleLoopRef } = loopUi;
  const playtestCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const playtestSessionRef = useRef<GuitarGameSession | null>(null);
  const chartRef = useRef<GuitarChart | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const errorCountRef = useRef(0);
  // Set just before the post-create navigate so the edit-load effect can
  // skip re-fetching a chart we already hold in memory (which would wipe
  // undo history). Cleared once consumed.
  const justSavedSlugRef = useRef<string | null>(null);

  useEffect(() => {
    scrollBeatRef.current = scrollBeat;
  }, [scrollBeat]);
  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);
  useEffect(() => {
    saveModePref('guitar');
  }, []);

  // ---- Slug derivation (mirrors the Pulse builder) ----
  useEffect(() => {
    if (slug.length === 0 && !slugManuallyEdited) {
      setSlug(`map-${Math.random().toString(36).slice(2, 8)}`);
    }
  }, [slug, slugManuallyEdited]);
  useEffect(() => {
    if (slugManuallyEdited || editingId) {
      return;
    }
    const derived = slugify(title);
    if (derived.length >= 2) {
      setSlug(derived);
    }
  }, [title, slugManuallyEdited, editingId]);

  // ---- Undo / redo history ----
  const applyHistory = useCallback((h: NotesHistory) => {
    historyRef.current = h;
    setHistory(h);
  }, []);

  const pushHistory = useCallback(
    (prev: ManiaNote[]) => {
      applyHistory({
        past: [...historyRef.current.past.slice(-HISTORY_LIMIT + 1), prev],
        future: [],
      });
    },
    [applyHistory],
  );

  /** Apply a discrete change with one undo entry. */
  const commitNotes = useCallback(
    (next: ManiaNote[]) => {
      pushHistory(notesRef.current);
      notesRef.current = next;
      setNotes(next);
    },
    [pushHistory],
  );

  /** Apply a change without an undo entry (mid-drag). */
  const liveNotes = useCallback((next: ManiaNote[]) => {
    notesRef.current = next;
    setNotes(next);
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.past.length === 0) {
      return;
    }
    const prev = h.past[h.past.length - 1];
    applyHistory({ past: h.past.slice(0, -1), future: [...h.future, notesRef.current] });
    notesRef.current = prev;
    setNotes(prev);
    setSelectedId(null);
  }, [applyHistory]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (h.future.length === 0) {
      return;
    }
    const next = h.future[h.future.length - 1];
    applyHistory({ past: [...h.past, notesRef.current], future: h.future.slice(0, -1) });
    notesRef.current = next;
    setNotes(next);
    setSelectedId(null);
  }, [applyHistory]);

  // ---- Chart assembly + validation ----
  const chart = useMemo<GuitarChart>(() => {
    return new ManiaChartBuilder({
      mode: 'guitar',
      id: slug,
      slug,
      title,
      description,
      bpm,
      difficulty: Math.max(1, Math.min(5, difficulty)) as Difficulty,
      variant: 'normal',
      totalBeats,
      palette,
      audio,
      approachMs,
    })
      .addNotes(notes)
      .build();
  }, [slug, title, description, bpm, difficulty, totalBeats, palette, audio, approachMs, notes]);

  const draftValue = useMemo<GuitarEditorDraft>(() => ({
    mode: 'guitar', title, description, slug, songSlug, audio, bpm, totalBeats,
    difficulty, isPublic, palette, notes, approachMs,
  }), [title, description, slug, songSlug, audio, bpm, totalBeats, difficulty, isPublic, palette, notes, approachMs]);
  const draft = useEditorDraft({
    storageKey: editorDraftKey(user?.uid ?? '', 'guitar', editingSlug),
    ready: !!user && (!editingSlug || loadedSlug === editingSlug),
    value: draftValue,
    isValid: isGuitarEditorDraft,
    onRestore: (restored) => {
      setTitle(restored.title);
      setDescription(restored.description);
      setSlug(editingSlug ?? restored.slug);
      setSlugManuallyEdited(true);
      setSongSlug(restored.songSlug);
      setAudio(restored.audio);
      setBpm(restored.bpm);
      setTotalBeats(restored.totalBeats);
      setDifficulty(restored.difficulty);
      setIsPublic(restored.isPublic);
      setPalette(restored.palette);
      setApproachMs(restored.approachMs);
      liveNotes(restored.notes);
      applyHistory({ past: [], future: [] });
      setSelectedId(null);
    },
  });

  useEffect(() => {
    chartRef.current = chart;
  }, [chart]);

  const validation = useMemo(() => validateChart(chart), [chart]);
  const errorCount = validation.issues.filter((i) => i.level === 'error').length;
  useEffect(() => {
    errorCountRef.current = errorCount;
  }, [errorCount]);

  // ---- Editor canvas rendering ----
  const redraw = useCallback(() => {
    const grid = gridRef.current;
    const canvas = canvasRef.current;
    if (!grid || !canvas) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    grid.draw({
      width: rect.width,
      height: rect.height,
      notes,
      scrollBeat,
      pxPerBeat,
      totalBeats,
      snap,
      selectedId,
      hover,
      palette,
      keyLabels,
      playing,
      loopRegion: shownLoopRegion,
      loopPlayheadBeat: preview.isLooping ? preview.playheadBeat : null,
    });
  }, [
    notes,
    scrollBeat,
    pxPerBeat,
    totalBeats,
    snap,
    selectedId,
    hover,
    palette,
    keyLabels,
    playing,
    shownLoopRegion,
    preview.isLooping,
    preview.playheadBeat,
  ]);

  useEffect(() => {
    if (canvasRef.current && !gridRef.current) {
      gridRef.current = new GuitarEditorGrid(canvasRef.current);
    }
    redraw();
  }, [redraw]);

  useEffect(() => {
    const onResize = () => redraw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [redraw]);

  // Forward playback scrolls the grid so the now-line follows the audio;
  // loop playback keeps the view fixed (the sweeping playhead moves instead),
  // so the author can place notes against the looped bar.
  useEffect(() => {
    if (playing && !preview.isLooping) {
      setScrollBeat(Math.min(preview.playheadBeat, totalBeats));
    }
  }, [playing, preview.isLooping, preview.playheadBeat, totalBeats]);

  // ---- Pointer interaction ----
  const currentGeo = useCallback((): EditorGeometry => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      scrollBeat: scrollBeatRef.current,
      pxPerBeat,
    };
  }, [pxPerBeat]);

  const clampBeat = useCallback(
    (b: number) => Math.max(0, Math.min(b, totalBeats)),
    [totalBeats],
  );

  const findNoteAt = useCallback((x: number, y: number, g: EditorGeometry): ManiaNote | null => {
    const lane = editorLaneFromX(x, g);
    if (lane === null) {
      return null;
    }
    let best: ManiaNote | null = null;
    let bestDist = Infinity;
    for (const n of notesRef.current) {
      if (n.lane !== lane) {
        continue;
      }
      const headY = editorBeatToY(n.beat, g);
      const dHead = Math.abs(y - headY);
      if (dHead <= HIT_TOLERANCE_PX && dHead < bestDist) {
        bestDist = dHead;
        best = n;
        continue;
      }
      if (n.holdEndBeat !== undefined) {
        const tailY = editorBeatToY(n.holdEndBeat, g);
        if (y >= tailY - HIT_TOLERANCE_PX && y <= headY + HIT_TOLERANCE_PX && bestDist > HIT_TOLERANCE_PX) {
          bestDist = HIT_TOLERANCE_PX;
          best = n;
        }
      }
    }
    return best;
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const g = currentGeo();

      // Left-edge gutter owns loop selection: left-drag sets the window,
      // right-click clears it. This works even while playing.
      if (x < LOOP_GUTTER_W) {
        if (e.button === 2) {
          preview.clearLoop();
          setLoopDraft(null);
          return;
        }
        if (e.button !== 0) {
          return;
        }
        canvas.setPointerCapture(e.pointerId);
        const anchorBeat = clampBeat(editorYToBeat(y, g));
        const region = buildLoopRegion(anchorBeat, anchorBeat, totalBeats);
        loopDragRef.current = { anchorBeat, region };
        setLoopDraft(region);
        return;
      }

      // Forward playback scrolls the grid, so placing on the moving surface
      // would be jittery — block it. Loop playback keeps the view fixed, so
      // authoring while you hear the bar is exactly the point.
      if (playing && !preview.isLooping) {
        return;
      }
      const lane = editorLaneFromX(x, g);
      if (lane === null) {
        return;
      }
      const hit = findNoteAt(x, y, g);
      if (e.button === 2) {
        if (hit) {
          commitNotes(notesRef.current.filter((n) => n.id !== hit.id));
          if (selectedIdRef.current === hit.id) {
            setSelectedId(null);
          }
        }
        return;
      }
      if (e.button !== 0) {
        return;
      }
      canvas.setPointerCapture(e.pointerId);
      if (hit) {
        setSelectedId(hit.id);
        dragRef.current = { id: hit.id, headBeat: hit.beat, base: notesRef.current.slice(), changed: false };
        return;
      }
      const beat = clampBeat(snapBeatToGrid(editorYToBeat(y, g), snap));
      // Coarse snap can land a click on an existing head that sat outside
      // the hit-test radius. Grab that note instead of stacking a
      // same-lane/same-beat duplicate the validator would silently reject.
      const dup = notesRef.current.find(
        (n) => n.lane === lane && Math.abs(n.beat - beat) < SAME_BEAT_EPSILON,
      );
      if (dup) {
        setSelectedId(dup.id);
        dragRef.current = { id: dup.id, headBeat: dup.beat, base: notesRef.current.slice(), changed: false };
        return;
      }
      const id = `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      const newNote: ManiaNote = { id, beat, lane };
      const base = notesRef.current.slice();
      liveNotes([...notesRef.current, newNote]);
      setSelectedId(id);
      dragRef.current = { id, headBeat: beat, base, changed: true };
    },
    [
      playing,
      preview.isLooping,
      preview.clearLoop,
      currentGeo,
      findNoteAt,
      commitNotes,
      clampBeat,
      snap,
      liveNotes,
      totalBeats,
    ],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const g = currentGeo();

      const loopDrag = loopDragRef.current;
      if (loopDrag) {
        const region = buildLoopRegion(loopDrag.anchorBeat, clampBeat(editorYToBeat(y, g)), totalBeats);
        loopDrag.region = region;
        setLoopDraft(region);
        setHover(null);
        return;
      }

      // No placement ghost over the loop gutter (clicks there start a loop drag,
      // not a note), but an in-progress note drag still tracks the cursor.
      const lane = x < LOOP_GUTTER_W ? null : editorLaneFromX(x, g);
      const beat = clampBeat(snapBeatToGrid(editorYToBeat(y, g), snap));
      setHover(lane === null ? null : { beat, lane });

      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const next = notesRef.current.map((n) => {
        if (n.id !== drag.id) {
          return n;
        }
        if (beat > drag.headBeat + 1e-6) {
          drag.changed = true;
          return { ...n, holdEndBeat: beat };
        }
        if (n.holdEndBeat !== undefined) {
          drag.changed = true;
        }
        const copy = { ...n };
        delete copy.holdEndBeat;
        return copy;
      });
      liveNotes(next);
    },
    [currentGeo, clampBeat, snap, liveNotes, totalBeats],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (canvas && canvas.hasPointerCapture(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }
      const loopDrag = loopDragRef.current;
      if (loopDrag) {
        loopDragRef.current = null;
        setLoopDraft(null);
        preview.startLoop(loopDrag.region);
        return;
      }
      const drag = dragRef.current;
      dragRef.current = null;
      if (drag && drag.changed) {
        pushHistory(drag.base);
      }
    },
    [pushHistory, preview.startLoop],
  );

  // Manual scroll (wheel) — attached natively so preventDefault isn't passive.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Scrolling moves the view. Forward playback fights the follow-scroll, so
      // pause it; a loop's view is independent, so keep looping while the
      // author scrolls around to look at other bars.
      const p = previewRef.current;
      if (p.isPlaying && !p.isLooping) {
        p.pause();
      }
      const delta = e.deltaY / pxPerBeat;
      setScrollBeat((prev) => Math.max(0, Math.min(prev + delta, totalBeats)));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [pxPerBeat, totalBeats]);

  const clearAll = useCallback(() => {
    if (notesRef.current.length === 0) {
      return;
    }
    if (!confirm('Clear all notes? You can undo this.')) {
      return;
    }
    commitNotes([]);
    setSelectedId(null);
  }, [commitNotes]);

  const changeSong = useCallback(
    (newSlug: string) => {
      if (newSlug === songSlug) {
        return;
      }
      const tpl = templateForSlug(newSlug);
      if (
        notesRef.current.length > 0 &&
        !confirm(
          `Switch backing song to "${tpl.title}"? BPM and length change to match the new track; notes past its end are removed.`,
        )
      ) {
        return;
      }
      setLoopDraft(null);
      setSongSlug(newSlug);
      setAudio(tpl.audio);
      setBpm(tpl.bpm);
      setTotalBeats(tpl.totalBeats);
      setPalette(tpl.palette);
      setScrollBeat(0);
      setSelectedId(null);
      // A song swap changes bpm + length; undoing across that boundary could
      // restore notes beyond the new track's end (invalid). Trim to the new
      // length and reset history rather than recording an undoable entry.
      const trimmed = notesRef.current.filter(
        (n) => n.beat <= tpl.totalBeats && (n.holdEndBeat ?? n.beat) <= tpl.totalBeats,
      );
      notesRef.current = trimmed;
      setNotes(trimmed);
      historyRef.current = { past: [], future: [] };
      setHistory({ past: [], future: [] });
    },
    [songSlug],
  );

  // ---- Save / delete ----
  const onSave = useCallback(async () => {
    setSaveError(null);
    if (savingRef.current || errorCountRef.current > 0 || notesRef.current.length === 0 || draft.pending || !user) {
      return;
    }
    const currentChart = chartRef.current;
    if (!currentChart) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    let savedDraft = draftValue;
    let savedMapSlug = currentChart.slug;
    try {
      if (editingId) {
        await updateMap(editingId, { chart: currentChart, isPublic });
        setSavedSlug(currentChart.slug);
      } else {
        try {
          const res = await createMap(currentChart, isPublic);
          setSavedSlug(res.slug);
          setEditingId(res.id);
          justSavedSlugRef.current = res.slug;
          setLoadedSlug(res.slug);
          setSlug(res.slug);
          setSlugManuallyEdited(true);
          navigate(modePath('guitar', `build/${res.slug}`), { replace: true });
        } catch (err) {
          if (!isSlugTaken(err)) {
            throw err;
          }
          const retrySlug = `${currentChart.slug}-${Math.random().toString(36).slice(2, 5)}`;
          savedDraft = { ...draftValue, slug: retrySlug };
          savedMapSlug = retrySlug;
          setSlug(retrySlug);
          setSlugManuallyEdited(true);
          const res = await createMap(
            { ...currentChart, id: retrySlug, slug: retrySlug },
            isPublic,
          );
          setSavedSlug(res.slug);
          setEditingId(res.id);
          justSavedSlugRef.current = res.slug;
          setLoadedSlug(res.slug);
          navigate(modePath('guitar', `build/${res.slug}`), { replace: true });
        }
      }
      draft.markSaved(savedDraft, editorDraftKey(user.uid, 'guitar', savedMapSlug));
    } catch (err) {
      setSaveError(formatSaveError(err as { body?: SaveErrorBody; message?: string }));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [draft.markSaved, draft.pending, draftValue, editingId, isPublic, navigate, user]);

  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  });

  const onDelete = useCallback(async () => {
    if (!editingId) {
      return;
    }
    if (!confirm('Delete this map? This cannot be undone.')) {
      return;
    }
    try {
      const res = await authFetch(`/api/maps/${editingId}`, { method: 'DELETE' });
      if (!res.ok) {
        throw new Error('Delete failed');
      }
      draft.markSaved(draftValue);
      navigate(modePath('guitar', 'browse'));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Delete failed');
    }
  }, [draft.markSaved, draftValue, editingId, navigate]);

  // ---- Playtest with the real game session ----
  const startPlaytest = useCallback(() => {
    if (errorCountRef.current > 0 || notesRef.current.length === 0) {
      return;
    }
    preview.pause();
    setPlaytestOutcome(null);
    setPlaytestNonce((n) => n + 1);
    setPlaytest(true);
  }, [preview]);

  const replayPlaytest = useCallback(() => {
    setPlaytestOutcome(null);
    setPlaytestNonce((n) => n + 1);
  }, []);

  const exitPlaytest = useCallback(() => {
    setPlaytest(false);
    setPlaytestOutcome(null);
  }, []);

  useEffect(() => {
    if (!playtest) {
      return;
    }
    const canvas = playtestCanvasRef.current;
    const liveChart = chartRef.current;
    if (!canvas || !liveChart) {
      return;
    }
    const session = new GuitarGameSession({
      chart: liveChart,
      canvas,
      audioOffsetMs: user?.audioOffsetMs ?? 0,
      enableLoops: false,
      onStatusChange: () => {},
      onComplete: (o) => setPlaytestOutcome(o),
    });
    playtestSessionRef.current = session;
    session.start().catch((err) => {
      console.error('Playtest failed to start:', err);
    });
    return () => {
      playtestSessionRef.current = null;
      void session.destroy();
    };
    // chartRef + user are read once at session start; we deliberately don't
    // restart on every edit or audio-offset change. playtestNonce is the
    // explicit restart trigger for "Play again".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playtest, playtestNonce]);

  // ---- Edit-existing load ----
  useEffect(() => {
    if (!editingSlug || !user?.uid) {
      return;
    }
    // We just created this map and already hold its full state in memory —
    // skip the round-trip so undo history (and any post-save edits) survive.
    if (justSavedSlugRef.current === editingSlug) {
      justSavedSlugRef.current = null;
      setLoadedSlug(editingSlug);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      try {
        const detail = await fetchMapDetail(editingSlug, { mode: 'guitar' });
        if (cancelled) {
          return;
        }
        if (detail.chart.mode !== 'guitar') {
          setSaveError(
            `Map "${detail.chart.slug}" is a ${detail.chart.mode} chart and can't be opened in the Guitar editor.`,
          );
          return;
        }
        if (detail.authorUid !== user.uid) {
          setSaveError('You can only edit maps you own.');
          return;
        }
        const c = detail.chart;
        setEditingId(detail.id);
        setTitle(c.title);
        setDescription(c.description);
        setSlug(c.slug);
        setSlugManuallyEdited(true);
        setBpm(c.bpm);
        setTotalBeats(c.totalBeats);
        setAudio(c.audio);
        setPalette(c.palette);
        setDifficulty(c.difficulty);
        setApproachMs(c.approachMs);
        setIsPublic(detail.isPublic);
        const matchTpl = SONG_TEMPLATES.find((t) => t.audio.src === c.audio.src);
        setSongSlug(matchTpl?.slug ?? DEFAULT_SONG_SLUG);
        const loaded = c.notes.map((n) => ({ ...n }));
        notesRef.current = loaded;
        setNotes(loaded);
        historyRef.current = { past: [], future: [] };
        setHistory({ past: [], future: [] });
        setLoadedSlug(editingSlug);
      } catch (err) {
        if (!cancelled) {
          setSaveError(err instanceof Error ? err.message : 'Failed to load map');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [editingSlug, user?.uid]);

  // ---- Global keyboard shortcuts ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (!savingRef.current) {
          void onSaveRef.current();
        }
        return;
      }
      if (inField) {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        togglePlayRef.current();
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        toggleLoopRef.current();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const id = selectedIdRef.current;
        if (id) {
          commitNotes(notesRef.current.filter((n) => n.id !== id));
          setSelectedId(null);
        }
        return;
      }
      const idx = Number(e.key);
      if (Number.isInteger(idx) && idx >= 1 && idx <= SNAP_DIVISIONS.length) {
        setSnap(SNAP_DIVISIONS[idx - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, commitNotes]);

  const holdCount = notes.filter((n) => n.holdEndBeat !== undefined).length;

  return (
    <div className="builder-page guitar-builder-page" data-testid="guitar-builder-page">
      {/*
        Loading is an overlay, not an early return: unmounting the editor
        subtree would replace the <canvas> node and orphan the
        GuitarEditorGrid (and the native wheel listener) bound to the old
        node, leaving a permanently blank grid after edit-load / first save.
      */}
      {loading && (
        <div className="guitar-editor-loading" data-testid="guitar-builder-loading">
          <p className="muted">Loading editor...</p>
        </div>
      )}
      <div className="builder-header">
        <div>
          <h2>{editingId ? 'Edit guitar map' : 'Build a guitar map'}</h2>
          <p className="muted faint">
            {notes.length} notes &middot; {holdCount} holds &middot; {totalBeats} beats
          </p>
        </div>
        <div className="builder-header-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={undo}
            disabled={history.past.length === 0}
            title="Undo (Ctrl+Z)"
            data-testid="guitar-builder-undo"
          >
            Undo
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={redo}
            disabled={history.future.length === 0}
            title="Redo (Ctrl+Shift+Z)"
          >
            Redo
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={startPlaytest}
            disabled={errorCount > 0 || notes.length === 0}
            data-testid="guitar-builder-playtest"
            title="Playtest with the real game"
          >
            Playtest
          </button>
          {editingId && (
            <button type="button" className="btn btn-danger" onClick={() => void onDelete()}>
              Delete
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void onSave()}
            disabled={saving || notes.length === 0 || errorCount > 0 || !!draft.pending}
            data-testid="guitar-builder-save"
            title={
              errorCount > 0
                ? `Fix ${errorCount} issue${errorCount === 1 ? '' : 's'} before saving`
                : 'Save (Ctrl+S)'
            }
          >
            {saving ? 'Saving...' : editingId ? 'Save changes' : 'Save'}
          </button>
        </div>
      </div>

      <EditorDraftNotice savedAt={draft.pending?.savedAt} isDirty={draft.isDirty} notice={draft.notice} storageError={draft.storageError} onRestore={draft.restore} onDiscard={draft.discard} />
      {saveError && (
        <div className="error-box" data-testid="guitar-builder-error" role="alert">
          {saveError}
        </div>
      )}
      {!editingId && <MyMapsList mode="guitar" variant="builder" />}

      <div className="builder-grid">
        <div className="card">
          <h3>Metadata</h3>
          <label>
            Title
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={48}
              data-testid="guitar-builder-title"
            />
          </label>
          <label>
            Slug (URL)
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(slugify(e.target.value));
                setSlugManuallyEdited(true);
              }}
              pattern="[a-z0-9-]+"
              minLength={2}
              maxLength={40}
              disabled={!!editingId}
              data-testid="guitar-builder-slug"
            />
          </label>
          <label>
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
              rows={3}
            />
          </label>
        </div>

        <div className="card">
          <h3>Song</h3>
          <label>
            Backing track
            <select
              value={songSlug}
              onChange={(e) => changeSong(e.target.value)}
              disabled={!!editingId}
              data-testid="guitar-builder-song"
            >
              {SONG_TEMPLATES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.title} ({c.bpm} BPM &middot; {c.totalBeats} beats)
                </option>
              ))}
            </select>
            <span className="muted faint">{audio.attribution}</span>
          </label>
          {editingId && (
            <span className="muted faint">Backing track is locked when editing an existing map.</span>
          )}
        </div>

        <div className="card">
          <h3>Feel</h3>
          <label>
            Difficulty
            <input
              type="range"
              min={1}
              max={5}
              value={difficulty}
              onChange={(e) => setDifficulty(Number(e.target.value))}
            />
            <span>{difficulty} / 5</span>
          </label>
          <label>
            Scroll speed (approach)
            <input
              type="range"
              min={GUITAR_APPROACH_MS_MIN}
              max={GUITAR_APPROACH_MS_MAX}
              step={50}
              value={approachMs}
              onChange={(e) => setApproachMs(Number(e.target.value))}
              data-testid="guitar-builder-approach"
            />
            <span>
              {approachMs} ms {approachMs <= 700 ? '(fast)' : approachMs >= 1600 ? '(slow)' : ''}
            </span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              data-testid="guitar-builder-public"
            />
            Public (visible in community browse)
          </label>
        </div>
      </div>

      <div className="card builder-track-card">
        <div className="builder-track-header">
          <h3>Chart</h3>
          <div className="builder-track-controls">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => togglePlay()}
              data-testid="guitar-builder-play"
              title="Play / pause preview (Space)"
            >
              {playing && !preview.isLooping ? 'Pause' : 'Play'}
            </button>
            <button
              type="button"
              className={`btn btn-ghost ${preview.isLooping ? 'active' : ''}`}
              onClick={toggleLoop}
              data-testid="guitar-builder-loop"
              aria-pressed={preview.isLooping}
              title="Loop the current section (L) — drag the left gutter to pick the bit"
            >
              {preview.isLooping ? 'Stop loop' : 'Loop'}
            </button>
            <span className="builder-control-separator" />
            <span className="muted">Snap:</span>
            {SNAP_DIVISIONS.map((d, i) => (
              <button
                key={d}
                type="button"
                className={`btn btn-ghost ${snap === d ? 'active' : ''}`}
                onClick={() => setSnap(d)}
                title={`1/${d} beat (${i + 1})`}
                aria-pressed={snap === d}
              >
                1/{d}
              </button>
            ))}
            <span className="builder-control-separator" />
            <span className="muted">Zoom:</span>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setPxPerBeat((p) => Math.max(PX_PER_BEAT_MIN, p - PX_PER_BEAT_STEP))}
              title="Zoom out"
            >
              &minus;
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setPxPerBeat((p) => Math.min(PX_PER_BEAT_MAX, p + PX_PER_BEAT_STEP))}
              title="Zoom in"
            >
              +
            </button>
            <span className="builder-control-separator" />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearAll}
              disabled={notes.length === 0}
              title="Clear all notes"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="guitar-editor-wrap">
          <canvas
            ref={canvasRef}
            className="guitar-editor-canvas"
            data-testid="guitar-builder-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={() => setHover(null)}
            onContextMenu={(e) => e.preventDefault()}
          />
          <input
            type="range"
            className="guitar-editor-scrollbar"
            min={0}
            max={Math.max(1, totalBeats)}
            step={0.25}
            value={scrollBeat}
            onChange={(e) => {
              if (preview.isPlaying && !preview.isLooping) {
                preview.pause();
              }
              setScrollBeat(Number(e.target.value));
            }}
            aria-label="Scrub through the chart"
            data-testid="guitar-builder-scrub"
          />
        </div>
        <p className="muted faint builder-instructions">
          Click a lane to place a note &middot; drag up from a note to make a hold &middot; click a
          note in another lane on the same beat for a chord &middot; right-click or select +{' '}
          <kbd>Del</kbd> to remove &middot; scroll / drag the slider to scrub &middot; <kbd>Space</kbd>{' '}
          to preview &middot; drag the green left gutter (or <kbd>L</kbd>) to loop a bar and place
          notes while it repeats
        </p>
      </div>

      {validation.issues.length > 0 && (
        <div className="card">
          <h3>Validation</h3>
          <ul className="validation-list">
            {validation.issues.slice(0, 8).map((issue, i) => (
              <li key={i}>
                {issue.level}: {issue.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {savedSlug && !saveError && !draft.isDirty && (
        <div className="card builder-saved" data-testid="guitar-builder-saved">
          Saved! <Link to={modePath('guitar', `play/${savedSlug}`)}>Play it</Link> or share{' '}
          <code>
            {window.location.origin}
            {modePath('guitar', `play/${savedSlug}`)}
          </code>
        </div>
      )}

      {playtest && (
        <div className="guitar-playtest-overlay" data-testid="guitar-builder-playtest-overlay">
          <div className="guitar-playtest-inner">
            <div className="guitar-playtest-header">
              <h3>Playtest — {title}</h3>
              <button type="button" className="btn" onClick={exitPlaytest}>
                Back to editor
              </button>
            </div>
            <div className="guitar-playtest-stage">
              <canvas
                ref={playtestCanvasRef}
                className="guitar-playtest-canvas"
                width={960}
                height={540}
              />
              {playtestOutcome && (
                <div
                  className="guitar-playtest-results"
                  data-testid="guitar-builder-playtest-results"
                >
                  <h4>{playtestOutcome.completed ? 'Playtest complete' : 'Playtest failed'}</h4>
                  <dl className="guitar-playtest-stats">
                    <div>
                      <dt>Score</dt>
                      <dd>{playtestOutcome.score.toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt>Accuracy</dt>
                      <dd>{(playtestOutcome.accuracy * 100).toFixed(1)}%</dd>
                    </div>
                    <div>
                      <dt>Max combo</dt>
                      <dd>{playtestOutcome.maxCombo}</dd>
                    </div>
                    <div>
                      <dt>Perfect</dt>
                      <dd>{playtestOutcome.perfects}</dd>
                    </div>
                    <div>
                      <dt>Good</dt>
                      <dd>{playtestOutcome.goods}</dd>
                    </div>
                    <div>
                      <dt>Miss</dt>
                      <dd>{playtestOutcome.misses}</dd>
                    </div>
                  </dl>
                  <div className="guitar-playtest-actions">
                    <button type="button" className="btn btn-primary" onClick={replayPlaytest}>
                      Play again
                    </button>
                    <button type="button" className="btn" onClick={exitPlaytest}>
                      Back to editor
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
